import { GenericErrorPage, NotFoundPage } from "@climatepartner/components/ErrorPages";
import { FunctionComponent } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { AnalyticsRoutes } from "../../modules/analytics/routes";
import { CommunicationRoutes } from "../../modules/communication/routes";
import { DocumentsRoutes } from "../../modules/documents/routes";
import { EconautDemoRoutes } from "../../modules/econaut-demo/routes";
import { HubRoutes } from "../../modules/hub/routes";
import { NetworkRoutes, SupplierFrontDoorRoutes } from "../../modules/network/routes";
import { ReductionRoutes } from "../../modules/reduction/routes";
import { HOMESCREEN_PATH, OAUTH_CALLBACK_PATH } from "../../shared/routing/paths/app";
import { AppLayout } from "../layout/app-layout";
import { AdminAreaRoutes } from "./admin-area";
import { CalculationRedirectRoute } from "./calculation-redirect";
import { CustomerAreaRoutes } from "./customer-area";
import { HomeEntryRoute } from "./home-entry-route";

const OAuthCallback = () => null;

const BaseRedirectRoute = {
  path: HOMESCREEN_PATH,
  element: <HomeEntryRoute />,
  children: [{ path: OAUTH_CALLBACK_PATH, element: <OAuthCallback /> }],
};

/**
 * A module keeps its own route tree in `<module>/routes.tsx` and this file only decides where that
 * tree hangs and what wraps it. Two entries are not modules but groupings of several — see
 * `customer-area.tsx` and `admin-area.tsx`.
 *
 * Order is for reading only: react-router ranks matches by path specificity, not by position.
 */
const routeTree = [
  // network       /network/supplier/**   the supplier front door, outside AppLayout; see
  //                                      `SupplierFrontDoorRoutes` in `modules/network/routes.tsx`
  ...SupplierFrontDoorRoutes,

  {
    element: <AppLayout />,
    errorElement: <GenericErrorPage />,
    children: [
      // app         /  and  /oauth/callback         home entry; see `home-entry-route.tsx`
      BaseRedirectRoute,
      // hub         /academy, /contribute, /solutions/*
      HubRoutes,
      // customer-dashboard + customer-emission-factors + pcf + ccf
      //             /customer/:customerId/**        grouped in `customer-area.tsx`
      CustomerAreaRoutes,
      // network     /network/**                     buyer and supplier, minus the three above
      NetworkRoutes,
      // reduction   /reduction/**
      ReductionRoutes,
      // analytics   /analytics
      AnalyticsRoutes,
      // documents   /documents
      DocumentsRoutes,
      // communication /communication/**
      CommunicationRoutes,
      // curated-calculation-factors + calculation-defaults
      //             /emission-factors/**, /default-values   grouped in `admin-area.tsx`
      AdminAreaRoutes,
      // app         /calculation                    redirect only; see `calculation-redirect.tsx`
      CalculationRedirectRoute,
      // econaut-demo /econaut-demo
      EconautDemoRoutes,
    ],
  },

  { path: "*", element: <NotFoundPage /> },
];

// Created on first render rather than at import: `initIntendedRoute` must read the entry URL first.
let router: ReturnType<typeof createBrowserRouter> | undefined;
const getRouter = () => (router ??= createBrowserRouter(routeTree));

export const Router: FunctionComponent = () => <RouterProvider router={getRouter()} />;
